// React component to render route info in a timeline of a segment

import React, { ReactNode, useEffect } from 'react';
import TwTimeline from '../../../TwTimeline/TwTimeline';
import StopInfo from '../StopInfo/StopInfo';
import StopStatusUpdateInput from '../StatusUpdateInput/StopStatusUpdateInput';
import { StopStatus, Trip, TRIP_STATUS, TripStop } from '../../../../redux/api/trips/trips.model';
import TwGenericMenu from '../../../TwGenericMenu/TwGenericMenu';
import Box from '@mui/material/Box';
import TwEdiMessageUpdate, {
  TwEdiMessageType
} from '../../../TwEdiMessageUpdate/TwEdiMessageUpdate';
import { TwLoadTooltipInfoProps } from '../../../TwLoadTooltipInfo/TwLoadTooltipInfo';
import useAuth from '../../../../providers/Auth/Auth.provider';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setTripDetailReload } from '../../../../redux/features/navigation/navigationSlice';
import { USER_ROLE } from '../../../../providers/User/User.model';

export interface SegmentRouteInfoProps {
  /**
   * segment
   * */
  segment: Trip;
  /**
   * all segments
   */
  allSegments?: Trip[];
  /**
   * load id to which segment belongs to
   * */
  loadId?: string;

  /**
   * startOrder
   */
  startOrder: number;

  /**
   * boolean to show/hide the status update input
   */
  showStatusUpdateInput?: boolean;

  /**
   * boolean to show/hide the add signature action button
   */
  showAddSignatureAction?: boolean;

  /**
   * boolean to show and restrict actions to customers
   */
  isCustomerLink: boolean;
  /**
   * Public load token
   */
  publicLoadToken?: string;
  /**
   * Callback called to reload Order data
   * */
  reloadData?: () => void;
  /**
   * Orders tooltip data
   */
  ordersTooltipDataArray?: TwLoadTooltipInfoProps[];
}

const SegmentRouteInfo: React.FC<SegmentRouteInfoProps> = ({
  segment,
  allSegments,
  loadId,
  startOrder,
  showStatusUpdateInput = true,
  showAddSignatureAction,
  isCustomerLink,
  publicLoadToken,
  ordersTooltipDataArray,
  reloadData
}): JSX.Element => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const dispatch = useAppDispatch();
  const [openEdiMessageUpdate, setOpenEdiMessageUpdate] = React.useState<boolean>(false);
  const [ediMessageUpdateType, setEdiMessageUpdateType] = React.useState<TwEdiMessageType>(
    TwEdiMessageType.DELAY_REASON
  );
  const [tripStopForEdiMessageUpdate, setTripStopForEdiMessageUpdate] = React.useState<
    TripStop | undefined
  >(undefined);

  const canMakeEdiUpdates = (): boolean => {
    const userRole = userData?.role;
    // if undefined then return false
    if (!userRole) {
      return false;
    }

    return [USER_ROLE.ADMIN, USER_ROLE.OWNER, USER_ROLE.DISPATCHER].includes(userRole);
  };

  useEffect(() => {
    if (!openEdiMessageUpdate) {
      setTripStopForEdiMessageUpdate(undefined);
    }
  }, [openEdiMessageUpdate]);

  const reloadOverlayDataAfterSignature = (): void => {
    if (isLoadDetailOpen) {
      dispatch(setTripDetailReload(true));
    }
    reloadData && reloadData();
  };

  const ediMessageUpdate: ReactNode = (
    <TwEdiMessageUpdate
      open={openEdiMessageUpdate}
      messageType={ediMessageUpdateType}
      onClose={(): void => setOpenEdiMessageUpdate(false)}
      onSuccess={(): void => {
        setOpenEdiMessageUpdate(false);
      }}
      tripStop={tripStopForEdiMessageUpdate}
    />
  );

  return (
    <TwTimeline stepStartNumber={startOrder} other={ediMessageUpdate}>
      {segment.stops.map((stop, index) => {
        // If segment order is 1, show add signature action for all stops,
        // else only show from the second to last stop of segment
        const showAddSignature = showAddSignatureAction && (startOrder === 1 || index > 0);

        // If Stop is first stop in segment, find the previous stop from the previous segment to the current segment in allSegments
        const prevStop =
          index === 0
            ? allSegments?.find((s) => s.order === segment.order - 1)?.stops.slice(-1)[0]
            : segment.stops[index - 1];

        // If Stop is last stop in segment, find the next stop from the next segment to the current segment in allSegments
        const nextStop =
          index === segment.stops.length - 1
            ? allSegments?.find((s) => s.order === segment.order + 1)?.stops[0]
            : segment.stops[index + 1];

        // Get the order readable id related to the stop
        const ltlOrderReadableId =
          enableLtl && stop.order_readable_id ? stop.order_readable_id : '';

        const enableSendCheckIn =
          stop.status === StopStatus.arrived || stop.status === StopStatus.complete;
        const enableSendCheckOut = stop.status === StopStatus.complete;

        return (
          <>
            <StopInfo
              key={`${segment.id}-${stop.id}`}
              prevStop={prevStop}
              stop={stop}
              nextStop={nextStop}
              loadId={loadId}
              LTLOrderReadableId={ltlOrderReadableId}
              ordersTooltipDataArray={ordersTooltipDataArray}
              publicLoadToken={publicLoadToken}
              isCustomerLink={isCustomerLink}
              nextStopStatus={
                segment.status !== TRIP_STATUS.DELIVERED
                  ? segment.stops[index + 1]?.status
                  : undefined
              }
              stopActionElement={
                <Box display="flex" alignItems="center">
                  {showStatusUpdateInput ? (
                    <StopStatusUpdateInput
                      tripId={segment.id || ''}
                      status={stop.status || StopStatus.notStarted}
                      stopId={stop.id || ''}
                      tripStatus={segment.status || TRIP_STATUS.DRAFT}
                      reloadData={reloadData}
                    />
                  ) : undefined}

                  {stop.edi_stop_identifier && canMakeEdiUpdates() && (
                    <TwGenericMenu
                      buttonSx={{
                        position: 'relative !important',
                        right: '0 !important'
                      }}
                      options={[
                        {
                          label: 'Confirm appt',
                          subLabel: 'EDI 214 (AA/AB)',
                          action: (): void => {
                            setEdiMessageUpdateType(TwEdiMessageType.CONFIRM_APPOINTMENT);
                            setTripStopForEdiMessageUpdate(stop);
                            setOpenEdiMessageUpdate(true);
                          }
                        },
                        {
                          label: 'Send check-in',
                          subLabel: 'EDI 214 (X3/X1)',
                          action: (): void => {
                            setEdiMessageUpdateType(TwEdiMessageType.SEND_CHECK_IN);
                            setTripStopForEdiMessageUpdate(stop);
                            setOpenEdiMessageUpdate(true);
                          },
                          disabled: !enableSendCheckIn,
                          tooltipText: enableSendCheckIn
                            ? undefined
                            : 'Stop must be arrived or completed to send check-in',
                          toolTipPlacement: 'left'
                        },
                        {
                          label: 'Send check-out',
                          subLabel: 'EDI 214 (AF/D1/CD)',
                          action: (): void => {
                            setEdiMessageUpdateType(TwEdiMessageType.SEND_CHECK_OUT);
                            setTripStopForEdiMessageUpdate(stop);
                            setOpenEdiMessageUpdate(true);
                          },
                          disabled: !enableSendCheckOut,
                          tooltipText: enableSendCheckOut
                            ? undefined
                            : 'Stop must be completed to send check-out',
                          toolTipPlacement: 'left'
                        },
                        {
                          label: 'Mark as delayed',
                          subLabel: 'EDI 214 (SD)',
                          action: (): void => {
                            setEdiMessageUpdateType(TwEdiMessageType.DELAY_REASON);
                            setTripStopForEdiMessageUpdate(stop);
                            setOpenEdiMessageUpdate(true);
                          }
                        }
                      ]}
                    />
                  )}
                </Box>
              }
              showAddSignatureAction={showAddSignature}
              onSignatureSaved={reloadOverlayDataAfterSignature}
              showCheckInAndOutTimes={getTenantSetting('show-check-in-and-check-out-times')}
            />
          </>
        );
      })}
    </TwTimeline>
  );
};

export default SegmentRouteInfo;
